import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import type { Token } from "../api/types";

export function useTokens(): {
  tokens: Token[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (body: unknown) => Promise<Token>;
  update: (id: string, body: unknown) => Promise<Token>;
  remove: (id: string) => Promise<void>;
} {
  const [tokens, setTokens] = useState<Token[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get<{ tokens: Token[] }>("/api/tokens");
      setTokens(res.tokens);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = useCallback(
    async (body: unknown) => {
      const res = await api.post<{ token: Token }>("/api/tokens", body);
      await refresh();
      return res.token;
    },
    [refresh],
  );

  const update = useCallback(
    async (id: string, body: unknown) => {
      const res = await api.put<{ token: Token }>(`/api/tokens/${id}`, body);
      await refresh();
      return res.token;
    },
    [refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await api.delete(`/api/tokens/${id}`);
      await refresh();
    },
    [refresh],
  );

  return { tokens, loading, error, refresh, create, update, remove };
}
